import { Suspense, useEffect, useMemo } from 'react';
import { Link, Redirect, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    SortingState,
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as ButtonGroup from '@givebutter/primitives/button-group';
import { Label } from '@givebutter/primitives/label';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { faMailbox, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CreateEngageMessageParams, createEngageMessage, fetchEngageMessages } from 'api/engage';
import Content from 'assets/js/components/Content';
import { MessageDetails } from 'assets/js/pages/engage/MessageDetails';
import { SplashScreen as EngageAccessSplash } from 'assets/js/pages/engage/SplashScreen';
import * as EngageIndexParts from 'assets/js/pages/engage/components/EngageIndexParts';
import { columnIds, createMessageColumns } from 'assets/js/pages/engage/messageColumnBuilder';
import { StatusFilter } from 'assets/js/pages/engage/types';
import { useEngageConfig } from 'assets/js/pages/engage/useEngageConfiguration';
import { getStatusFromFilter } from 'assets/js/pages/engage/util';
import { TextingGivebutterPlusSplashPage } from 'components/givebutter-plus-splash/TextingGivebutterPlusSplashPage';
import * as Input from 'components/input';
import * as SubNav from 'components/sub-nav';
import { EngageSubNavTabs } from 'components/sub-nav/EngageSubNavTabs';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { PLUS_MARKETING_LINK } from 'constants/links';
import { MessageStatus as StatusFilters } from 'constants/messages';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import VisualEditorCallout from 'images/visual-editor-callout.webp';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { capitalize } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { EngageMessage, EngageMessageChannel } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<EngageMessage>();

const ColumnAttributes = {
    [columnIds.CreatedOn]: 'created_at',
    [columnIds.Creator]: 'creator_full_name',
    [columnIds.Name]: 'internal_name',
    [columnIds.RecipientCount]: 'recipient_count',
    [columnIds.Sender]: 'sender',
    [columnIds.SendingOn]: 'sending_at',
    [columnIds.SentOn]: 'sent_at',
    [columnIds.Stats]: null,
    [columnIds.Type]: 'editor',
    [columnIds.UpdatedOn]: 'updated_at',
};

const EngageIndex = () => {
    const queryClient = useQueryClient();
    const { error } = useToast();
    const match = useRouteMatch();
    const { channel } = useParams<{ channel: EngageMessageChannel }>();
    const history = useHistory();
    const { engageConfig } = useEngageConfig();
    const { accountId, hasGivebutterPlusAccess, hasTextingAccess } = useCurrentAccount();
    const [search, setSearch] = usePersistentSearch();
    const debouncedSearch = useDebounce(search);
    const { engageMetrics = false } = useFlags();

    const {
        messageIndexTab,
        setMessageIndexTab,
        hasDismissedVisualEditorCallout,
        setHasDismissedVisualEditorCallout,
    } = useLocalStorage(state => state);
    const isDraft = messageIndexTab === StatusFilters.DRAFT;
    const isOutbox = messageIndexTab === StatusFilters.QUEUED;
    const isSent = messageIndexTab === StatusFilters.SENT;

    const clearFilterByKey = useFiltersCache(state => state.clearFilterByKey);
    const showVisualEditorCallout =
        channel === 'email' && !hasGivebutterPlusAccess && !hasDismissedVisualEditorCallout;

    const {
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnVisibility,
        setColumnVisibility,
    } = Table.useReactTableState({
        sortingState: getDefaultSortingState(messageIndexTab),
    });

    const sortingParams =
        sorting.length > 0
            ? {
                  [`sortBy${sorting[0].desc ? 'Desc' : ''}`]:
                      ColumnAttributes[sorting[0].id as columnIds],
              }
            : {};

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            query: debouncedSearch,
            status: getStatusFromFilter(messageIndexTab),
            channel: [channel] as EngageMessageChannel[],
            ...sortingParams,
        },
        500,
    );

    const { data: messages } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessagesWithFilters(accountId, params),
        queryFn: () => fetchEngageMessages(accountId, params),
    });

    const { mutate: createMessage } = useMutation({
        mutationFn: (values: CreateEngageMessageParams) => createEngageMessage(accountId, values),
        onSuccess: data => {
            clearFilterByKey('engage');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.engageMessages(accountId) });
            history.push(`/accounts/${accountId}/engage/${channel}/${data.id}/edit`);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unknown error occurred');
        },
    });

    const columns = useMemo(() => {
        const msgCols = createMessageColumns(columnHelper);

        const baseColumns: (typeof msgCols)[keyof typeof msgCols][] = [msgCols.name];

        if (channel === 'email') {
            baseColumns.push(msgCols.type);
        }

        if (isDraft) {
            return [
                ...baseColumns,
                msgCols.estimatedRecipients,
                msgCols.creator,
                msgCols.createdAt,
                msgCols.updatedAt,
                msgCols.action,
            ];
        }

        if (isOutbox) {
            return [
                ...baseColumns,
                msgCols.estimatedRecipients,
                msgCols.creator,
                msgCols.createdAt,
                msgCols.sender,
                msgCols.sendingAt,
                msgCols.action,
            ];
        }

        const sentCols = [
            ...baseColumns,
            msgCols.recipients,
            msgCols.creator,
            msgCols.createdAt,
            msgCols.sender,
            msgCols.sentAt,
            msgCols.states,
            msgCols.action,
        ];

        if (engageMetrics) {
            return sentCols;
        }

        return [msgCols.expand, ...sentCols];
    }, [isDraft, isOutbox, engageMetrics, channel]);

    const tableState = {
        pagination,
        sorting,
        columnVisibility,
    };

    const table = useReactTable({
        data: messages.data,
        columns,
        pageCount: messages.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowId: row => row.id.toString(),
        manualPagination: true,
        manualSorting: true,
        initialState: {
            ...tableState,
            columnPinning: {
                left: [Table.Constants.Expand],
            },
        },
        state: tableState,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        enableHiding: true,
        enableColumnPinning: true,
        meta: {
            to:
                isOutbox || (isSent && !engageMetrics)
                    ? undefined
                    : cell => {
                          if (isDraft) {
                              return `${match.url}/${cell.row.original.id}/edit`;
                          }
                          return `/accounts/${accountId}/engage/metrics/${channel}/${cell.row.original.id}`;
                      },
            renderExpandedRow: row => (
                <tr>
                    <td />
                    <td colSpan={row.getVisibleCells().length}>
                        <Suspense
                            fallback={
                                <div className={'p-8'}>
                                    <Loader variant={'static'} />
                                </div>
                            }
                        >
                            <MessageDetails message={row.original} />
                        </Suspense>
                    </td>
                </tr>
            ),
        },
    });

    useSyncPaginatedData(table, messages);

    useEffect(() => {
        const staleOtherTabs = queryClient.getQueryCache().findAll({
            queryKey: accountQueryKeys.engageMessages(accountId),
            stale: true,
            exact: false,
        });

        if (messages) {
            staleOtherTabs.forEach(query => {
                const possibleData = queryClient.getQueryData(query.queryKey);
                if (possibleData) {
                    query.setData({
                        ...possibleData,
                        counts: messages.counts,
                    });
                }
            });
        }
    }, [messages, accountId, queryClient]);

    const hasEngageAccess = engageConfig.status === 'active';

    if (channel === 'sms' && !hasGivebutterPlusAccess && !hasTextingAccess) {
        return <TextingGivebutterPlusSplashPage />;
    }

    if (['email', 'sms'].includes(channel) && !hasEngageAccess) {
        return <EngageAccessSplash />;
    }

    if (!['email', 'sms'].includes(channel)) {
        return <Redirect to={`/accounts/${accountId}/engage`} />;
    }

    return (
        <Content className="flex flex-col gap-y-4">
            <SubNav.Root>
                <SubNav.Header>
                    <SubNav.Title>Engage</SubNav.Title>
                    <SubNav.Actions>
                        <EngageIndexParts.TemplateDialog onTemplateSelection={createMessage} />
                    </SubNav.Actions>
                </SubNav.Header>
                <EngageSubNavTabs />
            </SubNav.Root>

            <div className={'flex flex-col gap-4'}>
                <EngageIndexParts.GettingStarted />
                {showVisualEditorCallout && (
                    <Box className="flex flex-col items-stretch overflow-hidden md:flex-row">
                        <div className="min-w-96 flex-shrink-0 basis-1/4 border-b border-gray-200 md:border-b-0 md:border-r">
                            <img
                                className="block min-h-full object-cover"
                                src={VisualEditorCallout}
                                alt="Givebutter Visual Editor"
                            />
                        </div>
                        <div className="relative flex flex-grow flex-col justify-center gap-4 p-6">
                            <div className="absolute right-0 top-0">
                                <Button
                                    variant={'transparent'}
                                    shape={'square'}
                                    onClick={() => setHasDismissedVisualEditorCallout(true)}
                                    aria-label="Dismiss"
                                >
                                    <FontAwesomeIcon
                                        size={'lg'}
                                        icon={faXmark}
                                        className="text-gray-500"
                                    />
                                </Button>
                            </div>
                            <div className="flex flex-col gap-1">
                                <img
                                    className={'max-w-28'}
                                    src={GivebutterPlusLogo}
                                    alt="Givebutter Plus logo"
                                />
                                <p className="text-lg font-medium">Visual editor</p>
                                <p className="text-sm text-gray-600">
                                    Create beautiful, professional emails with ease using our easy
                                    to use drag-and-drop visual editor.
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <a href={PLUS_MARKETING_LINK} target="_blank" rel="noreferrer">
                                    <Button variant="outline" color="neutral">
                                        Learn more
                                    </Button>
                                </a>
                                <Link to={`/accounts/${accountId}/settings/about/subscription`}>
                                    <Button variant="solid">Upgrade</Button>
                                </Link>
                            </div>
                        </div>
                    </Box>
                )}
                <div className={'flex flex-wrap items-center justify-between gap-4'}>
                    <ButtonGroup.Root
                        type="single"
                        value={messageIndexTab}
                        onValueChange={(value: StatusFilter) => {
                            setMessageIndexTab(value);
                            setSorting(getDefaultSortingState(value));
                        }}
                    >
                        <ButtonGroup.Item value={'sent'}>
                            <div className={'flex items-center gap-1.5'}>
                                <p>Sent</p>
                                {messages.counts.sent! > 0 && (
                                    <Badge
                                        size="sm"
                                        className={
                                            'group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200'
                                        }
                                    >
                                        {messages.counts.sent}
                                    </Badge>
                                )}
                            </div>
                        </ButtonGroup.Item>
                        <ButtonGroup.Item value={'queued'}>
                            <div className={'flex items-center gap-1.5'}>
                                <p>Outbox</p>
                                {messages.counts.queued! > 0 && (
                                    <Badge
                                        size="sm"
                                        className={
                                            'group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200'
                                        }
                                    >
                                        {messages.counts.queued}
                                    </Badge>
                                )}
                            </div>
                        </ButtonGroup.Item>
                        <ButtonGroup.Item value={'draft'}>
                            <div className={'flex items-center gap-1.5'}>
                                <p>Drafts</p>
                                {messages.counts.drafts! > 0 && (
                                    <Badge
                                        size="sm"
                                        className={
                                            'group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200'
                                        }
                                    >
                                        {messages.counts.drafts}
                                    </Badge>
                                )}
                            </div>
                        </ButtonGroup.Item>
                    </ButtonGroup.Root>
                </div>
                <Table.Root
                    key={messageIndexTab}
                    variant={'bordered'}
                    isLoading={isPending}
                    table={table}
                    searchTerm={debouncedSearch}
                >
                    <div className={'flex flex-col gap-y-4'}>
                        <Box className={'overflow-x-auto'}>
                            <div
                                className={
                                    'flex flex-wrap items-center justify-between gap-4 border-b border-b-gray-100 p-4 md:flex-nowrap'
                                }
                            >
                                <Label className="sr-only" htmlFor="engage_search">
                                    Search
                                </Label>
                                <Input.Root size={'sm'}>
                                    <Input.SearchIcon />
                                    <Input.Form
                                        id="engage_search"
                                        placeholder={'Search'}
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </Input.Root>
                                <div className="flex flex-wrap items-center gap-2.5">
                                    <Table.ResultsCount />
                                    <div className={'flex flex-wrap gap-1'}>
                                        <Table.ColumnVisibility
                                            transformIdToReadable={id => {
                                                const col = columns.find(col => col.id === id);
                                                const isString = typeof col?.header === 'string';

                                                const colOrId = isString ? col?.header : id;

                                                return capitalize(
                                                    `${colOrId}`.split('_').join(' '),
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <Table.Content>
                                <Table.Header />
                                <Table.Body />
                            </Table.Content>
                            <Table.Empty variant={'primary'}>
                                <Table.EmptyIcon>
                                    <FontAwesomeIcon icon={faMailbox} />
                                </Table.EmptyIcon>
                                <Table.EmptyTitle>No messages found</Table.EmptyTitle>
                                <Table.EmptyDescription>
                                    Start communicating more effectively with your supporters by
                                    creating your first outbound{' '}
                                    {channel === 'email' ? 'Email' : ' Text'}!
                                </Table.EmptyDescription>
                                <Table.EmptyWhenNotSearching>
                                    <div className={'mt-5'}>
                                        <EngageIndexParts.TemplateDialog
                                            onTemplateSelection={createMessage}
                                        />
                                    </div>
                                </Table.EmptyWhenNotSearching>
                            </Table.Empty>
                        </Box>
                        <Table.Pagination>
                            <Table.PaginationPageNavigation />
                        </Table.Pagination>
                    </div>
                </Table.Root>
            </div>
        </Content>
    );
};

const getDefaultSortingState = (filter: StatusFilter): SortingState => [
    { id: getDefaultSortFromFilter(filter), desc: true },
];

const getDefaultSortFromFilter = (status: StatusFilter): columnIds => {
    switch (status) {
        case StatusFilters.SENT:
            return columnIds.SentOn;
        case StatusFilters.QUEUED:
            return columnIds.SendingOn;
        case StatusFilters.DRAFT:
            return columnIds.UpdatedOn;
        default:
            return columnIds.CreatedOn;
    }
};

export { EngageIndex };
